/**
 * La pantalla de objetos, FUERA de un plano.
 *
 * Pedido de Daniel (12-oct-2026), mirando la pestaña «Mis objetos» recién
 * publicada: «¿cómo accedo a la interfaz de edición general, no dentro de un
 * plano?». La respuesta era «no se puede», y tenía razón en que ya molestaba.
 *
 * Por qué cambió el supuesto: cuando se eligió la pestaña dentro de la vitrina
 * —y fue la opción correcta entonces— las piezas vivían sólo en el navegador de
 * cada uno, así que el único lugar donde servían era adentro de un plano.
 * Desde que el catálogo vive en `ObjetosPlanta` y lo ve todo el equipo, entrar
 * por un plano cualquiera para administrar lo de todos quedó torcido.
 *
 * La diferencia con la pestaña no es el tamaño: es el ALCANCE.
 *
 * | | pestaña «Mis objetos» | esta pantalla |
 * |---|---|---|
 * | qué lista | sólo lo mío | el catálogo entero |
 * | sobre lo del equipo | nada | corregir y retirar |
 * | bajar el dibujo | no | SVG y PDF |
 *
 * Las dos se quedan. Corregir la medida de una pieza se nota JUSTO cuando se la
 * va a poner, y para eso la pestaña sigue siendo el lugar más corto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowCounterClockwise, ArrowLeft, DownloadSimple, Eye, EyeSlash, FilePlus,
  MagnifyingGlass, PencilSimple, Swap, Trash, Warning,
} from '@phosphor-icons/react';

import {
  GRUPO_PROPIO, MiniaturaMueble, apagarPieza, borrarPropio, catalogoVivo,
  editarPropio, encenderPieza, estaApagada,
  TINTA_ELECTRICO, esDeFabrica, esPropio, guardarCorreccion, guardarPropio,
  leerCompartidos,
} from './muebles';
import type { ModeloMueble } from './muebles';
import { SelectorGrupo } from './SelectorGrupo';
import { desdeArchivo } from './importar-simbolo';
import type { Trazo } from './importar-simbolo';
import { bajarObjeto } from './exportar-simbolo';
import {
  corregirCompartido, guardarAjustes, publicarObjeto, quitarCompartido,
  traerObjetos,
} from './objetos-servidor';

const cm = (v: number) => Math.round(v * 100);

/** De dónde sale una pieza. Decide qué se le puede hacer. */
type Procedencia = 'mio' | 'equipo' | 'fabrica';

const ROTULO: Record<Procedencia, string> = {
  mio: 'Mío', equipo: 'Del equipo', fabrica: 'De fábrica',
};

type Borrador = {
  clave: string | null;
  procedencia: Procedencia;
  nombre: string;
  /** En CENTÍMETROS, que es como el usuario piensa una medida de mueble. */
  w: string;
  h: string;
  grupo: string;
  trazo?: Trazo;
  /**
   * Si la pieza es del plano ELÉCTRICO.
   *
   * No es «pintala de café»: arrastra un paquete entero —se dibuja con la pluma
   * eléctrica, se cuenta aparte en el resumen, queda fuera del reconocimiento
   * automático de mobiliario (un punto de 20 cm en un plano arquitectónico daría
   * falso positivo) y puede llevar altura de montaje—.
   */
  electrico: boolean;
  /** Si se monta contra la cara de una pared. Un apagador sí; una luz de techo no. */
  pegaPared: boolean;
  /** Altura de montaje en CENTÍMETROS. Vacío = la pieza no rotula altura. */
  altura: string;
  /**
   * La ficha tal como estaba antes de abrir el formulario.
   *
   * Se guarda entera y no campo por campo porque una pieza trae cosas que este
   * formulario no muestra —el `simbolo` de fábrica, si es eléctrica, la altura—
   * y al corregirla hay que conservarlas: una corrección que sólo escribiera
   * nombre y medidas convertiría un tomacorriente en un mueble genérico.
   */
  base?: ModeloMueble;
};

/** Medida de arranque de un dibujo importado: 1 m en el lado largo. */
function medidaInicial(t: Trazo): { w: number; h: number } {
  const [, , cw, ch] = t.caja;
  const k = Math.max(cw, ch) || 1;
  return { w: Math.max(0.05, cw / k), h: Math.max(0.05, ch / k) };
}

/** Sin acentos y en minúscula, para que «Clóset» aparezca buscando «closet». */
const plano = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function AdminObjetos({ onCerrar }: { onCerrar: () => void }) {
  const [tick, setTick] = useState(0);
  const [puedePublicar, setPuedePublicar] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Procedencia | 'todas'>('todas');
  const [bo, setBo] = useState<Borrador | null>(null);
  const [queja, setQueja] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [tinta, setTinta] = useState(TINTA_ELECTRICO);
  const archivo = useRef<HTMLInputElement>(null);
  // El mismo `<input type=file>` sirve para importar una pieza nueva y para
  // cambiarle el dibujo a una que ya existe. Dos inputs serían dos caminos para
  // el mismo diálogo del sistema; lo que cambia es qué se hace con lo leído.
  const reemplazando = useRef(false);
  void tick;

  const refrescar = () => setTick((k) => k + 1);

  // El catálogo compartido se trae al abrir, igual que hace el editor: esta
  // pantalla se puede abrir sin haber entrado nunca a un plano en esta sesión,
  // y sin esto mostraría el catálogo del equipo vacío.
  useEffect(() => {
    let vivo = true;
    void traerObjetos().then((puede) => {
      if (!vivo) return;
      setPuedePublicar(puede);
      // `traerObjetos` ya le puso al módulo la tinta que dijo el servidor; esto
      // sólo la copia al selector, que no la puede leer solo porque el `<input
      // type=color>` es un control controlado por React.
      setTinta(TINTA_ELECTRICO);
      refrescar();
    });
    return () => { vivo = false; };
  }, []);

  const piezas = useMemo(() => {
    const delEquipo = new Set(leerCompartidos().map((m) => m.clave));
    const q = plano(busca.trim());
    return catalogoVivo()
      .map((m) => ({
        m,
        // El orden importa: una pieza propia que YA se publicó aparece en las
        // dos listas, y lo que manda es que es del equipo —es lo que decide si
        // corregirla toca el servidor o sólo este navegador—.
        p: (delEquipo.has(m.clave) ? 'equipo'
          : esPropio(m.clave) ? 'mio' : 'fabrica') as Procedencia,
        // Una pieza propia que además existe de fábrica no es una pieza nueva:
        // es una CORRECCIÓN, y por eso se le puede deshacer. La distinción no
        // se puede sacar del prefijo de la clave, porque corregir la conserva.
        pisada: esPropio(m.clave) && esDeFabrica(m.clave),
        apagada: estaApagada(m.clave),
      }))
      .filter(({ m, p }) =>
        (filtro === 'todas' || filtro === p)
        && (!q || plano(`${m.nombre} ${m.grupo}`).includes(q)));
  }, [busca, filtro, tick]);

  /**
   * Manda el color nuevo al servidor y repinta la pantalla.
   *
   * Si falla, el selector VUELVE al color que está vigente de verdad. Dejarlo
   * mostrando el elegido sería la peor mentira posible acá: Daniel vería el
   * café nuevo en su pantalla, entregaría el PDF, y el resto del equipo seguiría
   * dibujando con el viejo sin que nadie se entere.
   */
  async function aplicarTinta() {
    if (tinta.toLowerCase() === TINTA_ELECTRICO.toLowerCase()) return;
    try {
      await guardarAjustes({ tintaElectrico: tinta });
      setAviso('La pluma eléctrica cambió para todo el equipo.');
      refrescar();
    } catch (e) {
      setTinta(TINTA_ELECTRICO);
      setQueja(e instanceof Error ? e.message : 'No se pudo cambiar el color.');
    }
  }

  async function importar(f: File | undefined) {
    if (!f) return;
    setQueja(null);
    setLeyendo(true);
    try {
      const t = await desdeArchivo(f);
      if (reemplazando.current) {
        // Reemplazar cambia el DIBUJO y nada más: el nombre, la medida y el
        // grupo son del usuario, no del archivo. Pisarlos obligaría a volver a
        // escribirlos cada vez que se retoca la gráfica, que es justo lo que se
        // vino a hacer fácil.
        setBo((b) => (b ? { ...b, trazo: t } : b));
        return;
      }
      const { w, h } = medidaInicial(t);
      setBo({
        clave: null, procedencia: 'mio', trazo: t, grupo: GRUPO_PROPIO,
        nombre: f.name.replace(/\.(svg|pdf)$/i, '').replace(/[-_]+/g, ' ').trim().slice(0, 40),
        w: String(cm(w)), h: String(cm(h)),
        electrico: false, pegaPared: false, altura: '',
      });
    } catch (e) {
      setQueja(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
    } finally {
      setLeyendo(false);
      reemplazando.current = false;
      if (archivo.current) archivo.current.value = '';
    }
  }

  async function guardar() {
    if (!bo) return;
    const w = Number(bo.w.replace(',', '.')) / 100;
    const h = Number(bo.h.replace(',', '.')) / 100;
    const nombre = bo.nombre.trim();
    if (!nombre) { setQueja('Ponele un nombre.'); return; }
    // El piso es 1 cm y no «mayor que cero»: la columna del servidor guarda
    // tres decimales en metros, así que una medida más chica se escribe como
    // 0.000 y la pieza queda guardada pero invisible —se dibuja como un punto—
    // sin que nada avise. Más vale negarse acá que aceptar algo que no se ve.
    if (!(w >= 0.01) || !(h >= 0.01)) {
      setQueja('Las dos medidas tienen que ser de al menos 1 cm.'); return;
    }
    const grupo = bo.grupo.trim() || GRUPO_PROPIO;

    // La altura sólo existe si la pieza es eléctrica, y aun así es OPCIONAL:
    // `luz-techo` es el contraejemplo vivo del catálogo de fábrica —eléctrica,
    // no pega a ninguna pared y no rotula altura, porque flota en el centro del
    // cuarto—. Si la casilla fuera un solo campo, cada pieza nueva nacería
    // pegada a una pared y sin altura.
    let altura: number | undefined;
    if (bo.electrico && bo.altura.trim()) {
      const a = Number(bo.altura.replace(',', '.')) / 100;
      if (!(a >= 0) || a > 3) {
        setQueja('La altura de montaje tiene que estar entre 0 y 300 cm.'); return;
      }
      altura = a;
    }
    // Los tres se escriben con spread condicional y no como `electrico: false`
    // porque `ModeloMueble` los declara literales (`electrico?: true`): poner
    // `false` donde el catálogo de fábrica no pone nada haría que dos fichas
    // equivalentes no se parezcan al compararlas.
    const paquete = {
      ...(bo.electrico ? { electrico: true as const } : { electrico: undefined }),
      ...(bo.electrico && bo.pegaPared
        ? { pegaPared: true as const } : { pegaPared: undefined }),
      ...(altura != null ? { alturaDefecto: altura } : { alturaDefecto: undefined }),
    };
    setQueja(null);
    try {
      if (bo.procedencia === 'equipo' && bo.clave) {
        // Una pieza del equipo se corrige EN EL SERVIDOR: corregirla sólo acá
        // la dejaría distinta para cada uno, que es justo lo que publicarla
        // vino a evitar.
        const vieja = bo.base ?? catalogoVivo().find((x) => x.clave === bo.clave);
        if (!vieja) throw new Error('Esa pieza ya no está en el catálogo.');
        await corregirCompartido(bo.clave, { ...vieja, nombre, w, h, grupo, trazo: bo.trazo, ...paquete });
        setAviso(`«${nombre}» quedó corregido para todo el equipo.`);
      } else if (bo.procedencia === 'fabrica' && bo.clave && bo.base) {
        // Corregir una de fábrica NO cambia nada para el equipo: deja una copia
        // tuya que pisa a la original sólo en este navegador. Recién «Publicar»
        // la vuelve la de todos, y eso ya pasa por el rol que gobierna publicar.
        guardarCorreccion({ ...bo.base, nombre, w, h, grupo, trazo: bo.trazo, ...paquete });
        setAviso(`«${nombre}» quedó corregido para vos. Publicá si querés que lo vea el equipo.`);
      } else if (bo.clave) {
        editarPropio(bo.clave, { nombre, w, h, grupo, trazo: bo.trazo, ...paquete });
        setAviso(`«${nombre}» quedó actualizado.`);
      } else {
        guardarPropio({ nombre, w, h, grupo, simbolo: 'mueble', trazo: bo.trazo, ...paquete });
        setAviso(`«${nombre}» quedó en tu catálogo, bajo ${grupo}.`);
      }
      setBo(null);
      refrescar();
    } catch (e) {
      setQueja(e instanceof Error ? e.message : 'No se pudo guardar.');
    }
  }

  async function quitar(m: ModeloMueble, p: Procedencia) {
    const aviso = p === 'equipo'
      ? `¿Sacar «${m.nombre}» del catálogo de TODO el equipo?`
      : `¿Quitar «${m.nombre}» de tus objetos?`;
    // Se pregunta porque no hay deshacer: el catálogo no entra en la pila de
    // deshacer del plano, que es de la planta y no del usuario.
    if (!confirm(aviso)) return;
    try {
      if (p === 'equipo') await quitarCompartido(m.clave);
      else borrarPropio(m.clave);
      if (bo?.clave === m.clave) setBo(null);
      setAviso('La pieza salió del catálogo. Las que ya están en planos se quedan.');
      refrescar();
    } catch (e) {
      setQueja(e instanceof Error ? e.message : 'No se pudo quitar.');
    }
  }

  /**
   * Descarta la corrección y deja ver otra vez la pieza original.
   *
   * No «restaura» nada: la de fábrica nunca se fue, estaba tapada. Borrar la
   * copia la destapa, y los planos que la tengan puesta vuelven solos al
   * símbolo original porque siguen apuntando a la misma clave.
   */
  /**
   * Saca de circulación una pieza que no se puede borrar.
   *
   * Lo pidió Daniel el 15-oct-2026 con un caso concreto: la ducha de 50 × 90 no
   * existe en los modelos de Adelante, y mientras esté en el catálogo el
   * reconocedor se la pone a cada clóset que mida parecido. «Borrar» sería
   * mentir —la pieza vive en el código y vuelve en el próximo despliegue—, así
   * que se apaga: sale de la vitrina y del reconocedor, los planos que ya la
   * tienen puesta no se enteran, y se puede volver a encender.
   */
  function apagar(m: ModeloMueble) {
    if (!confirm(
      `¿Apagar «${m.nombre}»?\n\n`
      + 'Deja de ofrecerse al poner objetos y el reconocedor deja de elegirla '
      + 'al leer un plano. Los planos que ya la tienen puesta no cambian, y la '
      + 'podés volver a encender cuando quieras.',
    )) return;
    apagarPieza(m.clave);
    if (bo?.clave === m.clave) setBo(null);
    setAviso(`«${m.nombre}» quedó apagada. El reconocedor ya no la va a elegir.`);
    refrescar();
  }

  function encender(m: ModeloMueble) {
    encenderPieza(m.clave);
    setAviso(`«${m.nombre}» volvió al catálogo.`);
    refrescar();
  }

  function volverAFabrica(m: ModeloMueble) {
    if (!confirm(`¿Descartar tu corrección de «${m.nombre}» y volver al original?`)) return;
    borrarPropio(m.clave);
    if (bo?.clave === m.clave) setBo(null);
    setAviso(`«${m.nombre}» volvió al dibujo de fábrica.`);
    refrescar();
  }

  async function publicar(m: ModeloMueble) {
    setAviso(`Publicando «${m.nombre}»…`);
    try {
      await publicarObjeto(m);
      setAviso(`«${m.nombre}» ya está en el catálogo de todo el equipo.`);
    } catch (e) {
      setQueja(e instanceof Error ? e.message : 'No se pudo publicar la pieza.');
    }
    refrescar();
  }


  return (
    <div className="flex h-full flex-col bg-brand-bg" data-admin-objetos="">
      <header className="flex items-center gap-2 border-b border-stone-300 bg-white px-3 py-2">
        <button
          type="button" onClick={onCerrar} data-cerrar-admin=""
          aria-label="Volver"
          className="rounded p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-800"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <h1 className="text-sm font-bold text-brand-gray-500">Objetos</h1>
        <span className="text-xs tabular-nums text-brand-gray-400">{piezas.length}</span>
        <label className="ml-auto flex min-w-0 flex-1 items-center gap-1.5 rounded border
                          border-stone-300 px-2 py-1 focus-within:border-brand-green-100
                          sm:max-w-xs">
          <MagnifyingGlass size={14} weight="bold" className="shrink-0 text-stone-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar"
            data-buscar-objeto=""
            className="w-full min-w-0 text-xs outline-none"
          />
        </label>
      </header>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-stone-200
                      bg-white px-3 py-1.5">
        {(['todas', 'mio', 'equipo', 'fabrica'] as const).map((k) => (
          <button
            key={k} type="button" onClick={() => setFiltro(k)}
            data-filtro-objeto={k}
            className={`rounded-full px-2.5 py-1 text-[0.68rem] font-medium ${
              filtro === k
                ? 'bg-brand-green-100/30 text-stone-900'
                : 'text-stone-500 hover:bg-stone-100'
            }`}
          >
            {k === 'todas' ? 'Todos' : ROTULO[k]}
          </button>
        ))}
        {/* El color de la pluma eléctrica vive acá, al lado del filtro, y no
            dentro del formulario de una pieza: es una convención de dibujo del
            EQUIPO —vale para las 14 piezas del grupo a la vez—, no un atributo
            de ninguna de ellas. Por eso además lo gobierna el mismo rol que
            publica: si cada quien pudiera cambiarlo, el PDF que entrega cada
            persona saldría de un color distinto. */}
        {puedePublicar && (
          <label className="flex items-center gap-1.5 text-[0.68rem] text-stone-500"
                 title="Color de la pluma de TODO el grupo eléctrico, para todo el equipo">
            <input
              type="color" value={tinta}
              onChange={(e) => setTinta(e.target.value)}
              // Se guarda al SOLTAR y no en cada `change`: el selector del
              // sistema dispara un evento por cada pixel que se arrastra, y eso
              // serían cientos de PUT al servidor por un solo color elegido.
              onBlur={() => void aplicarTinta()}
              data-tinta-electrico=""
              className="h-6 w-8 cursor-pointer rounded border border-stone-300 bg-white p-0.5"
            />
            Pluma eléctrica
          </label>
        )}
        <button
          type="button"
          onClick={() => archivo.current?.click()}
          disabled={leyendo}
          data-importar-objeto=""
          className="ml-auto flex items-center gap-1.5 rounded border border-dashed
                     border-[#5f6f80] px-2.5 py-1 text-[0.68rem] font-medium
                     text-[#5f6f80] hover:bg-stone-100 disabled:opacity-50"
        >
          <FilePlus size={13} weight="bold" />
          {leyendo ? 'Leyendo…' : 'Importar dibujo'}
        </button>
        <input
          ref={archivo} type="file" accept=".svg,.pdf,image/svg+xml,application/pdf"
          className="hidden" data-archivo-objeto=""
          onChange={(e) => void importar(e.target.files?.[0])}
        />
      </div>

      {(queja || aviso) && (
        <p className={`flex items-start gap-1.5 px-3 py-1.5 text-[0.7rem] leading-tight ${
          queja ? 'bg-red-50 text-red-700' : 'bg-brand-yellow/10 text-brand-gray-500'
        }`} data-aviso-admin="">
          {queja && <Warning size={13} weight="bold" className="mt-px shrink-0" />}
          {queja ?? aviso}
        </p>
      )}

      {/* El formulario sirve a los tres caminos —importar, corregir lo mío y
          corregir lo del equipo— porque los campos son exactamente los mismos.
          Lo único que cambia es a dónde va el guardado. */}
      {bo && (
        <div className="border-b border-stone-300 bg-stone-50 px-3 py-2"
             data-forma-objeto={bo.clave ?? 'nuevo'}>
          <div className="mx-auto flex max-w-2xl flex-wrap items-end gap-2">
            {/* La previa lleva la ficha ORIGINAL por debajo para que una pieza
                de fábrica, que dibuja por `simbolo` y no por `trazo`, también
                se vea mientras se la corrige. */}
            <MiniaturaMueble
              px={44}
              modelo={{
                ...(bo.base ?? { clave: 'previa', simbolo: 'mueble' }),
                clave: 'previa', nombre: bo.nombre, grupo: bo.grupo,
                trazo: bo.trazo,
                // La previa obedece a la casilla y no a la ficha original: es lo
                // que hace que marcar «es eléctrico» se vea —la pieza cambia a
                // la pluma café ahí mismo— en vez de tener que guardar para
                // enterarse de qué hace la casilla.
                electrico: bo.electrico ? true : undefined,
                w: Math.max(0.01, Number(bo.w.replace(',', '.')) / 100 || 0.01),
                h: Math.max(0.01, Number(bo.h.replace(',', '.')) / 100 || 0.01),
              }}
            />
            <label className="flex min-w-[9rem] flex-1 flex-col gap-0.5 text-[0.6rem] text-stone-500">
              Nombre
              <input
                autoFocus value={bo.nombre}
                onChange={(e) => setBo({ ...bo, nombre: e.target.value })}
                data-nombre-objeto=""
                className="rounded border border-stone-300 px-1.5 py-1 text-xs
                           outline-none focus:border-[#5f6f80]"
              />
            </label>
            {([['w', 'Ancho'], ['h', 'Fondo']] as const).map(([k, rotulo]) => (
              <label key={k} className="flex w-20 flex-col gap-0.5 text-[0.6rem] text-stone-500">
                {rotulo} (cm)
                <input
                  value={bo[k]} inputMode="numeric"
                  onChange={(e) => setBo({ ...bo, [k]: e.target.value })}
                  data-medida-objeto={k}
                  className="rounded border border-stone-300 px-1.5 py-1 text-xs
                             tabular-nums outline-none focus:border-[#5f6f80]"
                />
              </label>
            ))}
            <SelectorGrupo
              id="admin" valor={bo.grupo} onAviso={setAviso}
              onCambio={(g) => setBo({ ...bo, grupo: g })}
            />

            {/* Las tres casillas van juntas y las dos de abajo sólo aparecen con
                la primera encendida, porque fuera de lo eléctrico no significan
                nada: el ancla a la cara de pared y la altura de montaje son
                conceptos del plano eléctrico, no de un sofá. */}
            <label className="flex items-center gap-1.5 self-center text-[0.7rem]
                              text-stone-600">
              <input
                type="checkbox" checked={bo.electrico}
                onChange={(e) => setBo({ ...bo, electrico: e.target.checked })}
                data-es-electrico=""
                className="h-3.5 w-3.5 accent-[#8a5a3b]"
              />
              Es eléctrico
            </label>
            {bo.electrico && (
              <>
                <label className="flex items-center gap-1.5 self-center text-[0.7rem]
                                  text-stone-600">
                  <input
                    type="checkbox" checked={bo.pegaPared}
                    onChange={(e) => setBo({ ...bo, pegaPared: e.target.checked })}
                    data-pega-pared=""
                    className="h-3.5 w-3.5 accent-[#8a5a3b]"
                  />
                  Pega a la pared
                </label>
                <label className="flex w-24 flex-col gap-0.5 text-[0.6rem] text-stone-500">
                  Altura (cm)
                  <input
                    value={bo.altura} inputMode="numeric" placeholder="sin altura"
                    onChange={(e) => setBo({ ...bo, altura: e.target.value })}
                    data-altura-objeto=""
                    title="Altura de montaje sobre el piso. Vacío para una pieza que no la rotula, como una luz de techo."
                    className="rounded border border-stone-300 px-1.5 py-1 text-xs
                               tabular-nums outline-none placeholder:text-stone-300
                               focus:border-[#5f6f80]"
                  />
                </label>
              </>
            )}
            {/* El pedido textual: «sustituir una imagen por otra». Antes el
                dibujo sólo se podía poner al crear la pieza, así que retocar la
                gráfica obligaba a borrarla y volver a empezar —y eso dejaba
                huérfanas las que ya estaban puestas en un plano—. */}
            <button
              type="button"
              onClick={() => { reemplazando.current = true; archivo.current?.click(); }}
              disabled={leyendo}
              data-reemplazar-dibujo=""
              title="Cambiar el dibujo por el de un SVG o PDF"
              className="flex items-center gap-1 rounded border border-stone-300 px-2.5
                         py-1.5 text-[0.7rem] text-stone-600 hover:bg-stone-100
                         disabled:opacity-50"
            >
              <Swap size={13} weight="bold" />
              {bo.trazo ? 'Cambiar dibujo' : 'Poner dibujo'}
            </button>
            <button
              type="button" onClick={() => void guardar()} data-guardar-objeto=""
              className="rounded bg-[#5f6f80] px-3 py-1.5 text-[0.7rem] font-medium
                         text-white hover:bg-[#4d5c6b]"
            >
              Guardar
            </button>
            <button
              type="button" onClick={() => { setBo(null); setQueja(null); }}
              data-cancelar-objeto=""
              className="rounded border border-stone-300 px-2.5 py-1.5 text-[0.7rem]
                         text-stone-600 hover:bg-stone-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        {!piezas.length ? (
          <p className="py-10 text-center text-xs text-stone-400">
            Ningún objeto con ese nombre.
          </p>
        ) : (
          <ul className="mx-auto grid max-w-4xl gap-1.5
                         sm:grid-cols-2 lg:grid-cols-3">
            {piezas.map(({ m, p, pisada, apagada }) => (
              <li key={m.clave}
                  className={`flex items-center gap-2 rounded border border-stone-200
                             bg-white px-2 py-1.5${apagada ? ' opacity-50' : ''}`}
                  data-objeto={m.clave} data-procedencia={p}
                  data-pisada={pisada ? '' : undefined}
                  data-apagada={apagada ? '' : undefined}>
                <MiniaturaMueble modelo={m} px={34} />
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-[0.72rem] leading-tight
                                   ${apagada ? 'text-stone-400 line-through' : 'text-stone-700'}`}>
                    {m.nombre}
                  </span>
                  <span className="block truncate text-[0.6rem] leading-tight
                                   tabular-nums text-stone-400">
                    {cm(m.w)}×{cm(m.h)} · {m.grupo}
                    {p !== 'fabrica' && ` · ${pisada ? 'Corregido' : ROTULO[p]}`}
                    {/* Se dice con palabras y no sólo con el gris: una fila
                        atenuada se lee como «deshabilitada por ahora», no como
                        una decisión que alguien tomó y puede revertir. */}
                    {apagada && ' · Apagada'}
                  </span>
                </span>

                {/* Bajar el dibujo es lo único que se puede hacer con CUALQUIER
                    pieza, de fábrica incluida: es el punto de partida para
                    dibujar otra con la misma gráfica, que fue el pedido. */}
                {(['svg', 'pdf'] as const).map((f) => (
                  <button
                    key={f} type="button"
                    onClick={() => bajarObjeto(m, f)}
                    data-bajar-objeto={`${m.clave}:${f}`}
                    title={`Descargar ${f.toUpperCase()}`}
                    className="flex items-center gap-0.5 rounded px-1 py-1 text-[0.55rem]
                               font-semibold uppercase text-stone-400
                               hover:bg-stone-100 hover:text-stone-700"
                  >
                    <DownloadSimple size={12} weight="bold" />
                    {f}
                  </button>
                ))}

                {p === 'mio' && puedePublicar && (
                  <button
                    type="button" onClick={() => void publicar(m)}
                    data-publicar-objeto={m.clave}
                    title={pisada
                      ? 'Publicar la corrección para todo el equipo'
                      : 'Publicar para todo el equipo'}
                    className="rounded px-1.5 py-1 text-[0.6rem] font-medium
                               text-[#5f6f80] hover:bg-stone-200"
                  >
                    {pisada ? 'Publicar corrección' : 'Publicar'}
                  </button>
                )}

                {/* Editar está en TODAS, de fábrica incluidas. Corregir una de
                    fábrica no la toca: guarda una copia tuya con la misma clave
                    que la pisa sólo acá —por eso al lado va «volver al de
                    fábrica» en lugar de la papelera, que no borraría nada—. */}
                {(p !== 'equipo' || puedePublicar) && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setQueja(null);
                        setBo({
                          clave: m.clave, procedencia: p, nombre: m.nombre,
                          grupo: m.grupo, w: String(cm(m.w)), h: String(cm(m.h)),
                          trazo: m.trazo, base: m,
                          electrico: m.electrico === true,
                          pegaPared: m.pegaPared === true,
                          altura: m.alturaDefecto == null ? '' : String(cm(m.alturaDefecto)),
                        });
                      }}
                      data-editar-objeto={m.clave}
                      aria-label={`Editar ${m.nombre}`}
                      className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                    >
                      <PencilSimple size={13} weight="bold" />
                    </button>
                    {pisada ? (
                      <button
                        type="button" onClick={() => void volverAFabrica(m)}
                        data-volver-fabrica={m.clave}
                        aria-label={`Volver ${m.nombre} al de fábrica`}
                        title="Descartar tu corrección y volver al original"
                        className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                      >
                        <ArrowCounterClockwise size={13} weight="bold" />
                      </button>
                    ) : p !== 'fabrica' ? (
                      <button
                        type="button" onClick={() => void quitar(m, p)}
                        data-borrar-objeto={m.clave}
                        aria-label={`Quitar ${m.nombre}`}
                        className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    ) : (
                      /* La de fábrica no se borra —vive en el código y volvería
                         en el próximo despliegue— pero SÍ se apaga. Era el
                         agujero que Daniel señaló: no había ninguna manera de
                         sacar del reconocedor una pieza que no existe. */
                      <button
                        type="button"
                        onClick={() => (apagada ? encender(m) : apagar(m))}
                        data-apagar-objeto={m.clave}
                        aria-label={`${apagada ? 'Encender' : 'Apagar'} ${m.nombre}`}
                        title={apagada
                          ? 'Devolverla al catálogo'
                          : 'Sacarla de la vitrina y del reconocedor'}
                        className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                      >
                        {apagada
                          ? <Eye size={13} weight="bold" />
                          : <EyeSlash size={13} weight="bold" />}
                      </button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
